import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface AuthInputProps {
  id: string;
  label: string;
  type: "email" | "password" | "text";
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: string;
  labelClassName?: string;
  inputClassName?: string;
  showPasswordToggle?: boolean;
  ariaDescribedBy?: string;
}

export function AuthInput({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  disabled = false,
  required = true,
  placeholder,
  error,
  labelClassName = "",
  inputClassName = "",
  showPasswordToggle = false,
  ariaDescribedBy,
}: AuthInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordField = type === "password";
  const inputType = isPasswordField && showPasswordToggle && showPassword ? "text" : type;

  const describedBy = [
    error ? `${id}-error` : null,
    ariaDescribedBy,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div className="grid gap-2">
      <Label
        htmlFor={id}
        className={`text-sm font-medium ${labelClassName}`}
      >
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={`border rounded-md p-2 focus:ring-2 focus:ring-primary ${
            error ? "border-red-500" : ""
          } ${isPasswordField && showPasswordToggle ? "pr-10" : ""} ${inputClassName}`}
        />
        {isPasswordField && showPasswordToggle && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3"
            onClick={() => setShowPassword((prev) => !prev)}
            disabled={disabled}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="text-red-500 text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
